import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useSapSyncToast, type SapSyncResult } from "../lib/use-sap-sync-toast";

const KEY = "companies";

export function useCompanyList(options?: {
	status?: string | undefined;
	search?: string | undefined;
	createdAtFrom?: string | undefined;
	createdAtTo?: string | undefined;
	country?: string | undefined;
	hasCountry?: string | undefined;
	salesPerson?: string | undefined;
	page?: number;
	limit?: number;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "list", options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.status) params.set("status", options.status);
			if (options?.search) params.set("search", options.search);
			if (options?.createdAtFrom) params.set("createdAtFrom", options.createdAtFrom);
			if (options?.createdAtTo) params.set("createdAtTo", options.createdAtTo);
			if (options?.country) params.set("country", options.country);
			if (options?.hasCountry) params.set("hasCountry", options.hasCountry);
			if (options?.salesPerson) params.set("salesPerson", options.salesPerson);
			return api.get<{ items: Company[]; total: number }>(`/api/companies?${params.toString()}`);
		},
	});
}

export function useCompanyCountries(options?: {
	status?: string | undefined;
	search?: string | undefined;
	createdAtFrom?: string | undefined;
	createdAtTo?: string | undefined;
	hasCountry?: string | undefined;
}) {
	return useQuery({
		queryKey: [KEY, "countries", options],
		queryFn: () => {
			const params = new URLSearchParams();
			if (options?.status) params.set("status", options.status);
			if (options?.search) params.set("search", options.search);
			if (options?.createdAtFrom) params.set("createdAtFrom", options.createdAtFrom);
			if (options?.createdAtTo) params.set("createdAtTo", options.createdAtTo);
			if (options?.hasCountry) params.set("hasCountry", options.hasCountry);
			return api.get<{ items: string[] }>(`/api/companies/countries?${params.toString()}`);
		},
		staleTime: 5 * 60 * 1000,
	});
}

export function useCompanySalesPersonIds(options?: {
	status?: string | undefined;
	search?: string | undefined;
	createdAtFrom?: string | undefined;
	createdAtTo?: string | undefined;
	country?: string | undefined;
	hasCountry?: string | undefined;
}) {
	return useQuery({
		queryKey: [KEY, "salespersons", options],
		queryFn: () => {
			const params = new URLSearchParams();
			if (options?.status) params.set("status", options.status);
			if (options?.search) params.set("search", options.search);
			if (options?.createdAtFrom) params.set("createdAtFrom", options.createdAtFrom);
			if (options?.createdAtTo) params.set("createdAtTo", options.createdAtTo);
			if (options?.country) params.set("country", options.country);
			if (options?.hasCountry) params.set("hasCountry", options.hasCountry);
			return api.get<{ items: string[] }>(`/api/companies/salespersons?${params.toString()}`);
		},
		staleTime: 5 * 60 * 1000,
	});
}

export function useCompanyDetail(id: string, accountId?: string) {
	return useQuery({
		queryKey: [KEY, "detail", id, accountId ?? null],
		queryFn: () =>
			api.get<Company>(
				`/api/companies/${id}${accountId ? `?account=${encodeURIComponent(accountId)}` : ""}`,
			),
		enabled: !!id,
	});
}

export function useCreateCompany() {
	const qc = useQueryClient();
	const reportSapSync = useSapSyncToast("partner", "createSuccess");
	return useMutation({
		mutationFn: (data: CreateCompanyInput) =>
			api.post<Company & SapSyncResult>("/api/companies", data),
		onSuccess: (company) => {
			reportSapSync(company);
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
		},
	});
}

export function useUpdateCompany() {
	const qc = useQueryClient();
	const reportSapSync = useSapSyncToast("partner");
	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: string;
			data: Partial<Omit<Company, "address">> & { address?: CompanyAddressInput };
		}) => api.patch<Company & SapSyncResult>(`/api/companies/${id}`, data),
		onSuccess: (company, vars) => {
			reportSapSync(company);
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", vars.id] });
		},
	});
}

export interface SimilarSapCard {
	sapCardCode: string;
	cardName: string;
	currency: string | null;
	taxNumber: string | null;
	companyId: string | null;
}

export interface SapCardByTax {
	sapCardCode: string;
	cardName: string;
	currency: string | null;
	taxNumber: string | null;
	groupCode: number | null;
	phone: string | null;
	fax: string | null;
	email: string | null;
	website: string | null;
	address: {
		street: string | null;
		city: string | null;
		state: string | null;
		postalCode: string | null;
		country: string | null;
	} | null;
	companyId: string | null;
}

export function useSapCardsByTax(taxNumber: string, enabled: boolean) {
	const trimmed = taxNumber.trim();
	return useQuery({
		queryKey: [KEY, "sap-by-tax", trimmed],
		queryFn: () =>
			api.get<{ items: SapCardByTax[] }>(
				`/api/companies/sap-by-tax?taxNumber=${encodeURIComponent(trimmed)}`,
			),
		// VKN 10, TCKN 11 hane.
		enabled: enabled && trimmed.length >= 10,
		staleTime: 60 * 1000,
	});
}

export function useSimilarSapCards(name: string, enabled: boolean) {
	const trimmed = name.trim();
	return useQuery({
		queryKey: [KEY, "sap-similar", trimmed],
		queryFn: () =>
			api.get<{ items: SimilarSapCard[] }>(
				`/api/companies/sap-similar?name=${encodeURIComponent(trimmed)}`,
			),
		enabled: enabled && trimmed.length >= 3,
		staleTime: 60 * 1000,
	});
}

export function useSapCurrencies() {
	return useQuery({
		queryKey: ["sap", "currencies"],
		queryFn: () => api.get<{ code: string; name: string }[]>("/api/sap/currencies"),
		staleTime: 5 * 60 * 1000,
	});
}

export function useSapStates(countryCode?: string) {
	return useQuery({
		queryKey: ["sap", "states", countryCode ?? "TR"],
		queryFn: () =>
			api.get<{ code: string; name: string }[]>(
				`/api/sap/states?country=${encodeURIComponent(countryCode ?? "TR")}`,
			),
		staleTime: 60 * 60 * 1000,
	});
}

export function useSapCountries() {
	return useQuery({
		queryKey: ["sap", "countries"],
		queryFn: () => api.get<{ code: string; name: string }[]>("/api/sap/countries"),
		staleTime: 60 * 60 * 1000,
	});
}

export function useSapBpGroups() {
	return useQuery({
		queryKey: ["sap", "bp-groups"],
		queryFn: () => api.get<{ code: number; name: string }[]>("/api/sap/bp-groups"),
		staleTime: 5 * 60 * 1000,
	});
}

export function useSapBpSeries() {
	return useQuery({
		queryKey: ["sap", "bp-series"],
		queryFn: () =>
			api.get<{ series: number; name: string; nextCode: string | null; isManual: boolean }[]>(
				"/api/sap/bp-series",
			),
		staleTime: 5 * 60 * 1000,
	});
}

export function useSalesPersons() {
	return useQuery({
		queryKey: ["sap", "salespersons"],
		queryFn: () =>
			api.get<{ id: string; sapSlpCode: number; name: string }[]>("/api/sap/salespersons"),
		staleTime: 5 * 60 * 1000,
	});
}

export function useAssignableUsers(options?: { enabled?: boolean }) {
	return useQuery({
		queryKey: [KEY, "assignable-users"],
		queryFn: () => api.get<{ id: string; name: string }[]>("/api/companies/assignable-users"),
		staleTime: 5 * 60 * 1000,
		enabled: options?.enabled !== false,
	});
}

export interface CompanyAddressInput {
	street?: string | undefined;
	city?: string | undefined;
	state?: string | undefined;
	postalCode?: string | undefined;
	country?: string | undefined;
}

export interface CreateCompanyInput {
	legalName: string;
	tradeName?: string | undefined;
	status: "lead" | "customer" | "vendor" | "inactive";
	sapSeries?: number | undefined;
	sapGroupCode?: number | undefined;
	currency?: string | undefined;
	currencies?: string[] | undefined;
	taxNumber?: string | undefined;
	taxOffice?: string | undefined;
	email?: string | undefined;
	phone?: string | undefined;
	fax?: string | undefined;
	website?: string | undefined;
	industry?: string | undefined;
	notes?: string | undefined;
	ownerId?: string | undefined;
	address?: CompanyAddressInput | undefined;
}

export interface CompanyAddress {
	id: string;
	type: "billing" | "shipping" | "hq";
	addressName: string | null;
	addressName2: string | null;
	addressName3: string | null;
	street: string | null;
	city: string | null;
	state: string | null;
	postalCode: string | null;
	country: string | null;
	isPrimary: boolean;
}

interface Owner {
	id: string;
	firstName: string | null;
	lastName: string | null;
}

export interface Company {
	id: string;
	owner: Owner | null;
	legalName: string;
	address?: CompanyAddress | null;
	/** Fatura/merkez adresine göre belirlenen SAP ülke kodu (örn. "TR"). */
	country?: string | null;
	tradeName: string | null;
	taxNumber: string | null;
	taxOffice: string | null;
	registrationNumber: string | null;
	industry: string | null;
	size: string | null;
	website: string | null;
	phone: string | null;
	email: string | null;
	fax: string | null;
	balance: string | null;
	currency: string | null;
	currencies?: string[] | null;
	notes: string | null;
	status: "lead" | "customer" | "vendor" | "inactive";
	sapCardCode: string | null;
	sapGroupCode: number | null;
	sapCurrency: string | null;
	sapLicTradNum: string | null;
	sapBalance: string | null;
	sapCreditLimit: string | null;
	lastSyncedAt: string | null;
	syncStatus: string | null;
	syncError?: string | null;
	ownerId: string | null;
	teamId: string | null;
	visibility: string;
	createdAt: string;
	updatedAt: string;
}

export interface CreateAddressInput {
	type: "billing" | "shipping" | "hq";
	addressName: string;
	addressName2?: string | undefined;
	addressName3?: string | undefined;
	street?: string | undefined;
	city?: string | undefined;
	state?: string | undefined;
	postalCode?: string | undefined;
	country?: string | undefined;
}

export function useRetryCompanySync() {
	const qc = useQueryClient();
	const reportSapSync = useSapSyncToast("partner");
	return useMutation({
		mutationFn: (id: string) => api.post<SapSyncResult>(`/api/companies/${id}/retry-sync`, {}),
		onSuccess: (result, id) => {
			reportSapSync(result);
			qc.invalidateQueries({ queryKey: [KEY, "detail", id] });
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
		},
	});
}

export function useCompanyAddresses(companyId: string, type?: "billing" | "shipping" | "hq") {
	return useQuery({
		queryKey: [KEY, companyId, "addresses", type],
		queryFn: () =>
			api.get<{ items: CompanyAddress[] }>(
				`/api/companies/${companyId}/addresses${type ? `?type=${type}` : ""}`,
			),
		enabled: !!companyId,
	});
}

export function useCreateCompanyAddress(companyId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateAddressInput) =>
			api.post<CompanyAddress>(`/api/companies/${companyId}/addresses`, data),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: [KEY, companyId, "addresses"] });
		},
	});
}

export function useUpdateShippingAddress(companyId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ addressId, data }: { addressId: string; data: Partial<CreateAddressInput> }) =>
			api.patch<CompanyAddress>(`/api/companies/${companyId}/addresses/${addressId}`, data),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: [KEY, companyId, "addresses"] });
		},
	});
}

export function useDeleteShippingAddress(companyId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (addressId: string) =>
			api.delete(`/api/companies/${companyId}/addresses/${addressId}`),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: [KEY, companyId, "addresses"] });
		},
	});
}

export function useAddressDistricts(stateCode?: string) {
	return useQuery({
		queryKey: ["address-districts", stateCode ?? ""],
		queryFn: () =>
			api.get<{ items: string[] }>(
				`/api/companies/address-districts${stateCode ? `?state=${encodeURIComponent(stateCode)}` : ""}`,
			),
		enabled: !!stateCode,
		staleTime: 60 * 60 * 1000,
	});
}
